import type {OrganizationalUnit} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnit";
import type {OrganizationalUnitTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnitTemplate";
import type {User} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {processOttopilotStringTemplate} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotStringTemplate";

export type OuPathEntry = {
    name: string;
    type: string;
    templateId: Uuid;
    ouId: Uuid;
};

export type OuHierarchySingle = {
    kind: "single";
    path: Array<OuPathEntry>;
    breakdown: Record<string, string>;
};

export type OuHierarchyAggregatedLevel = {
    type: string;
    templateId: Uuid;
    items: Array<{name: string; id: Uuid}>;
};

export type OuHierarchyAggregated = {
    kind: "aggregated";
    levels: Array<OuHierarchyAggregatedLevel>;
};

export type OuHierarchyData = OuHierarchySingle | OuHierarchyAggregated | {kind: "empty"};

/**
 * Normalize user's OU column value to an array of leaf OU ids.
 * Column can be single OU (Uuid) or multi (Array<Uuid>).
 */
export function getOuIdsFromUser(user: User, ouColumnId: Uuid | null): Array<Uuid> {
    if (ouColumnId === null) {
        return [];
    }
    const raw = user.additionalColumnValues[ouColumnId];
    if (raw === null || raw === undefined) {
        return [];
    }
    if (Array.isArray(raw)) {
        return raw
            .filter((id): id is string => typeof id === "string")
            .map((id) => id as Uuid);
    }
    if (typeof raw === "string") {
        return [raw as Uuid];
    }
    return [];
}

function getOuDisplayName(ou: OrganizationalUnit, organizationalUnitTemplates: Array<OrganizationalUnitTemplate>): string {
    const template = organizationalUnitTemplates.find((t) => t.id === ou.templateId);
    return template !== undefined ? processOttopilotStringTemplate(template.instanceDisplayNameTemplate, ou.additionalColumnValues) : String(ou.id);
}

function getTemplateDisplayName(templateId: Uuid, organizationalUnitTemplates: Array<OrganizationalUnitTemplate>): string {
    const template = organizationalUnitTemplates.find((t) => t.id === templateId);
    return template !== undefined ? template.displayName : "OU";
}

/**
 * Build path from leaf OU to root (returns root-first array).
 */
export function getPathFromLeaf(
    leafOuId: Uuid,
    organizationalUnits: Array<OrganizationalUnit>,
    organizationalUnitTemplates: Array<OrganizationalUnitTemplate>,
): Array<OuPathEntry> {
    const result: Array<OuPathEntry> = [];
    let ouId: Uuid | null = leafOuId;
    while (ouId !== null) {
        const ou = organizationalUnits.find((u) => u.id === ouId);
        if (ou === undefined) {
            break;
        }
        const template = organizationalUnitTemplates.find((t) => t.id === ou.templateId);
        const name = template !== undefined ? processOttopilotStringTemplate(template.instanceDisplayNameTemplate, ou.additionalColumnValues) : String(ou.id);
        const type = template !== undefined ? template.displayName : "OU";
        result.push({
            name,
            type,
            templateId: ou.templateId,
            ouId: ou.id,
        });
        ouId = ou.parentId;
    }
    return result.reverse();
}

function pathsEqual(a: Array<OuPathEntry>, b: Array<OuPathEntry>): boolean {
    if (a.length !== b.length) {
        return false;
    }
    for (let i = 0; i < a.length; i++) {
        const leftEntry = a[i];
        const rightEntry = b[i];
        if (leftEntry === undefined || rightEntry === undefined) {
            return false;
        }
        if (leftEntry.ouId !== rightEntry.ouId) {
            return false;
        }
    }
    return true;
}

/**
 * Build OU hierarchy display data: single path (Image 2) or aggregated (Image 1).
 */
export function buildOuHierarchyData(
    ouIds: Array<Uuid>,
    organizationalUnits: Array<OrganizationalUnit>,
    organizationalUnitTemplates: Array<OrganizationalUnitTemplate>,
): OuHierarchyData {
    if (ouIds.length === 0) {
        return {kind: "empty"};
    }

    const paths = ouIds.map((id) => getPathFromLeaf(id, organizationalUnits, organizationalUnitTemplates)).filter((p) => p.length > 0);

    if (paths.length === 0) {
        return {kind: "empty"};
    }

    const singlePath = paths[0];
    if (singlePath === undefined) {
        return {kind: "empty"};
    }
    const allSamePath = paths.length === 1 || paths.every((p) => pathsEqual(p, singlePath));

    if (allSamePath && singlePath.length > 0) {
        const breakdown: Record<string, string> = {};
        for (const entry of singlePath) {
            breakdown[entry.type] = entry.name;
        }
        return {
            kind: "single",
            path: singlePath,
            breakdown,
        };
    }

    const templateOrder = new Map<Uuid, number>();
    let order = 0;
    for (const p of paths) {
        for (const entry of p) {
            if (!templateOrder.has(entry.templateId)) {
                templateOrder.set(entry.templateId, order++);
            }
        }
    }

    const byTemplate = new Map<Uuid, {type: string; items: Map<Uuid, string>}>();
    for (const p of paths) {
        for (const entry of p) {
            let slot = byTemplate.get(entry.templateId);
            if (slot === undefined) {
                const template = organizationalUnitTemplates.find((t) => t.id === entry.templateId);
                slot = {
                    type: template !== undefined ? template.displayName : "OU",
                    items: new Map(),
                };
                byTemplate.set(entry.templateId, slot);
            }
            slot.items.set(entry.ouId, entry.name);
        }
    }

    const levels: Array<OuHierarchyAggregatedLevel> = [];
    const sortedTemplateIds = Array.from(templateOrder.entries())
        .sort((a, b) => a[1] - b[1])
        .map(([id]) => id);
    for (const templateId of sortedTemplateIds) {
        const slot = byTemplate.get(templateId);
        if (slot === undefined) {
            continue;
        }
        levels.push({
            type: slot.type,
            templateId,
            items: Array.from(slot.items.entries()).map(([id, name]) => ({
                name,
                id,
            })),
        });
    }

    return {kind: "aggregated", levels};
}
